/**
 * Versioned terms of use — shared client logic (MYAC-824).
 *
 * The in-force terms pointer lives at `system/public_config.terms`; the app reads
 * it to decide whether a user must accept or re-accept before continuing. The
 * terms *text* for that version is loaded from `terms_versions/{current_version}`
 * (see `appStore.loadInForceTerms`), so no version string is ever bundled with the
 * app. Ordering is always by `sequence` (a monotonic int) — the version string is
 * an opaque key and is never parsed.
 */

export interface TermsPointerLike {
  current_version?: string
  current_sequence?: number
  minimum_accepted_sequence?: number
}

/**
 * Whether a user who last accepted `acceptedSequence` must (re-)accept the
 * terms before continuing. Mirrors `TermsConfig.requires_consent` in
 * core_models_services:
 *  - no terms in force yet (`current_sequence <= 0`) → never blocks;
 *  - never consented is treated as sequence `0`, so gating is driven entirely by
 *    `minimum_accepted_sequence` (set it to force everyone, leave it below to
 *    grandfather users with no prior record);
 *  - otherwise blocks only when the accepted sequence predates the minimum
 *    required one (i.e. a material change was published since).
 */
export function requiresConsent(
  pointer: TermsPointerLike | null | undefined,
  acceptedSequence: number | null | undefined,
): boolean {
  const currentSequence = pointer?.current_sequence ?? 0
  if (!pointer || currentSequence <= 0) return false
  const seq = acceptedSequence ?? 0
  return seq < (pointer.minimum_accepted_sequence ?? 0)
}
